import { getTenant } from '../../../../../../utils/apis/tenants_repository';
import { BookingsData, getBookings } from '../../../../../../utils/apis/bookings_repository';
import React from "react";
import { Autocomplete, TextField } from '@mui/material';
import { Text } from '../../../../Text';
import { UserModel } from '../../../../../../utils/apis/user_repository';
import { Dialog, DialogContent } from '../../../../Dialog';
const { Button } = require('../../../../CustomButton');

//  for applied / other users, groups and tenants -> show AutoComplete_Input field
//  pass applied and non applied users, groups and tenants to each field
//  store values here in the state. Pass it down with submit option. pass new rule up to the parent component

interface VipProps {
    userList: UserModel[];
    appliedUsers: string[];
    setAppliedUsers: React.Dispatch<React.SetStateAction<string[]>>;
    setDataChanged: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Vip: React.FC<VipProps> = ({ userList, appliedUsers, setAppliedUsers, setDataChanged }) => {
    const [showWarning, setShowWarning] = React.useState<boolean>(false);
    const [holdTempUsers, setHoldTempUsers] = React.useState<string[]>([]);
    const [affectedBookings, setAffectedBookings] = React.useState<BookingsData[]>([]);
    const [exceededCategory, setExceededCategory] = React.useState<string>('');

    const handleChange = async (value: string[]) => {
        const newUser = value.filter(name => !appliedUsers.includes(name));
        if (newUser.length > 0) {
            console.log('newUser', newUser);
            const newUserObj = userList.filter(user => newUser.includes(user.name));
            const tenant = await getTenant(newUserObj[0].tenant);
            const bookings = await getBookings(tenant.name);
            const bookingsToShow: BookingsData[] = [];

            const perDayBookingCounts = bookings.reduce((acc, booking) => {
                const date = new Date(booking.startTime).toISOString().split('T')[0]
                acc[date] = acc[date] ? acc[date] + 1 : 1;
                return acc;
            }, {} as { [key: string]: number });

            const maxPerDayBookingCount = Math.max(...Object.values(perDayBookingCounts));

            // Find days where allocation would be exceeded after adding the new VIP
            const newVipCount = appliedUsers.length + 1;
            const overAllocatedDates = Object.entries(perDayBookingCounts)
                .filter(([, count]) => count + newVipCount > tenant.totalAllocation)
                .map(([date]) => date);

            // Only show bookings on over-allocated days
            bookings.filter(booking => {
                const date = new Date(booking.startTime).toISOString().split('T')[0];
                return overAllocatedDates.includes(date);
            }).forEach(booking => bookingsToShow.push(booking));

            if (tenant.totalAllocation <= appliedUsers.length) {
                setExceededCategory('vips');
                setHoldTempUsers(value);
                setShowWarning(true);
            } else if (tenant.totalAllocation <= maxPerDayBookingCount + appliedUsers.length) {
                setExceededCategory('bookings');
                setAffectedBookings(bookingsToShow.map(booking => booking));
                setHoldTempUsers(value);
                setShowWarning(true);
            } else {
                setAppliedUsers(value);
                setDataChanged(true);
            }
        } else {
            console.log(value)
            setAppliedUsers(value);
            setDataChanged(true);
        }
    }

    const cancelProceed = () => {
        setHoldTempUsers([]);
        setShowWarning(false);
    }

    const handleProceed = () => {
        setAppliedUsers(holdTempUsers);
        setDataChanged(true);
        setShowWarning(false);
    }

    return (
        <>
            <Text className="mt-10" as="h3">
                Applied Users
            </Text>
            <Autocomplete
                disablePortal
                id="users"
                options={userList.map(user => user.name)}
                multiple
                value={appliedUsers}
                onChange={(event, value) => {
                    handleChange(value);
                }}
                sx={{ width: 300 }}
                renderInput={(params) => <TextField {...params} label="Select Users" />}
                className='my-4'
            />
            <Dialog open={showWarning} onOpenChange={(value) => {
                setShowWarning(value);
            }}>
                {exceededCategory === 'bookings' && (
                    <DialogContent className="max-w-md">
                        <Text as="h3" className="text-center">Warning</Text>
                        <Text as="span" className="text-center block mb-2 text-sm text-gray-600">
                            You are at capacity for the following days. Below are the bookings for these days. Please review and delete one booking for each day before adding the VIP, or click Proceed to continue anyway.
                        </Text>
                        <div className="max-h-72 overflow-y-auto border rounded p-2 mb-4">
                            {Object.entries(
                                affectedBookings.reduce((acc, booking) => {
                                    const dateKey = new Date(booking.startTime).toISOString().split('T')[0];
                                    if (!acc[dateKey]) acc[dateKey] = [];
                                    acc[dateKey].push(booking);
                                    return acc;
                                }, {} as { [date: string]: BookingsData[] })
                            )
                            .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
                            .map(([dateKey, bookings]) => {
                                const date = new Date(dateKey).toLocaleDateString('en-AU', {
                                    weekday: 'short',
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                });
                                return (
                                <div key={date} className="mb-3">
                                    <div className="font-semibold text-sm bg-gray-100 px-2 py-1 rounded mb-1">
                                        {date} ({bookings.length} booking{bookings.length > 1 ? 's' : ''})
                                    </div>
                                    {bookings.map((booking, index) => (
                                        <div key={booking.bookingId || index} className="py-1 text-sm pl-2">
                                            {booking.bookingId.substring(0, 3)}...{booking.bookingId.substring(booking.bookingId.length - 4)} — {booking.email}
                                        </div>
                                    ))}
                                </div>
                            );
                            })}
                        </div>
                        <Text as="span" className="text-center block text-sm text-gray-500 mb-4">
                            {affectedBookings.length} booking(s) affected across {Object.keys(
                                affectedBookings.reduce((acc, booking) => {
                                    const date = new Date(booking.startTime).toISOString().split('T')[0];
                                    acc[date] = true;
                                    return acc;
                                }, {} as { [date: string]: boolean })
                            ).length} day(s)
                        </Text>
                        <div className="flex flex-row justify-center space-x-4">
                            <Button onClick={() => cancelProceed()} variant="secondary" className="w-44 border-primary text-primaryText">Cancel</Button>
                            <Button onClick={() => handleProceed()} variant="secondary" className="w-44 border-primary text-primaryText">Proceed</Button>
                        </div>
                    </DialogContent>
                )}
                {exceededCategory === 'vips' && (
                    <DialogContent>
                        <Text as="h3" className="text-center">Warning</Text>
                        <Text as="span" className="text-center">
                            <b>Number of VIPs cannot exceed the total allocation. </b>
                        </Text>
                        <div className="flex flex-row justify-center space-x-4 mt-6">
                            <Button onClick={() => setShowWarning(false)} variant="secondary" className="w-44 border-primary text-primaryText">OK</Button>
                        </div>
                    </DialogContent>
                )}
            </Dialog>
        </>
    );
};

export default Vip;